import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Info, UserPlus } from "lucide-react";
import {
  getOrganizationAdmin,
  updateOrganizationAdminSettings,
  getOrganizationMembers,
  addOrganizationMember,
} from "../../utils/Api";
import OrgSettingsSkeleton from "../../components/SkeletonLoading/OrgSettingsSkeleton";
import AddMemberModal from "../../modals/AddMemberModal";
import InvitationUrlModal from "../../modals/InvitationUrlModal";
import { useSendEmail } from "../../hooks/useSendEmail";
import { organizationInvitationEmail } from "../../emails";

interface OrganizationAdmin {
  id: string;
  name: string;
  show_text: boolean;
  show_molecules: boolean;
  show_tables: boolean;
  show_drawings: boolean;
  created_at: string;
}

interface Member {
  id: string;
  user_id: string | null;
  db_user_id: string | null;
  email: string | null;
  name?: string | null;
  role: string;
  status: string;
  created_at: string;
}

type ToggleKey =
  | "show_text"
  | "show_molecules"
  | "show_tables"
  | "show_drawings";

const TOGGLES: { key: ToggleKey; label: string; description: string }[] = [
  {
    key: "show_text",
    label: "Text",
    description:
      "Allow members to display text bounding boxes on documents. When off, the Text toggle is locked off for everyone in this organisation.",
  },
  {
    key: "show_molecules",
    label: "Molecules",
    description:
      "Allow members to display molecule bounding boxes on documents. When off, the Molecules toggle is locked off for everyone in this organisation.",
  },
  {
    key: "show_tables",
    label: "Tables",
    description:
      "Allow members to display table bounding boxes on documents. When off, the Tables toggle is locked off for everyone in this organisation.",
  },
  {
    key: "show_drawings",
    label: "Drawings",
    description:
      "Show molecule and reaction structure drawings in the Preview tab. When off, these drawings are hidden for everyone in this organisation.",
  },
];

const getRoleBadgeColor = (role: string) => {
  switch (role.toLowerCase()) {
    case "admin":
      return "bg-red-100 text-red-800";
    case "member":
      return "bg-blue-100 text-blue-800";
    default:
      return "bg-gray-100 text-gray-800";
  }
};

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const AdminOrganizationSettings: React.FC = () => {
  const { organizationId } = useParams<{ organizationId: string }>();
  const navigate = useNavigate();
  const { sendEmail } = useSendEmail();

  const [organization, setOrganization] = useState<OrganizationAdmin | null>(
    null,
  );
  const [members, setMembers] = useState<Member[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Add member state
  const [isAddMemberModalOpen, setIsAddMemberModalOpen] = useState(false);
  const [newMemberEmail, setNewMemberEmail] = useState("");
  const [newMemberRole, setNewMemberRole] = useState("member");
  const [addMemberError, setAddMemberError] = useState<string | null>(null);
  const [addMemberLoading, setAddMemberLoading] = useState(false);

  // Invitation URL modal state
  const [invitationUrl, setInvitationUrl] = useState<string | null>(null);
  const [isInvitationModalOpen, setIsInvitationModalOpen] = useState(false);
  const [isCopied, setIsCopied] = useState(false);

  const loadMembers = async () => {
    if (!organizationId) return;
    try {
      const response = await getOrganizationMembers(organizationId);
      setMembers(response.data.members);
    } catch (err: any) {
      console.error("Failed to load members:", err);
    }
  };

  useEffect(() => {
    if (!organizationId) return;
    let cancelled = false;

    (async () => {
      try {
        setLoading(true);
        const [orgResponse, membersResponse] = await Promise.all([
          getOrganizationAdmin(organizationId),
          getOrganizationMembers(organizationId),
        ]);
        if (!cancelled) {
          setOrganization(orgResponse.data);
          setMembers(membersResponse.data.members);
        }
      } catch (err: any) {
        if (!cancelled)
          setError(err.response?.data?.detail || "Failed to load organisation");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [organizationId]);

  const handleToggle = async (key: ToggleKey) => {
    if (!organization || saving) return;
    const newValue = !organization[key];
    setOrganization({ ...organization, [key]: newValue });
    setSaving(true);
    try {
      await updateOrganizationAdminSettings(organizationId!, {
        [key]: newValue,
      });
    } catch (err: any) {
      setOrganization({ ...organization, [key]: !newValue });
      setError(err.response?.data?.detail || "Failed to update settings");
    } finally {
      setSaving(false);
    }
  };

  const handleAddMember = async () => {
    if (!newMemberEmail.trim()) {
      setAddMemberError("Please enter an email address");
      return;
    }

    setAddMemberLoading(true);
    setAddMemberError(null);

    try {
      const response = await addOrganizationMember(
        organizationId!,
        newMemberEmail,
        newMemberRole,
      );

      await loadMembers();

      if (response.data.invitation_url) {
        await sendEmail({
          to: newMemberEmail,
          template: organizationInvitationEmail({
            organizationName: organization?.name || "",
            invitationUrl: response.data.invitation_url,
          }),
        });

        setInvitationUrl(response.data.invitation_url);
        setIsInvitationModalOpen(true);
        setIsCopied(false);
      }

      setNewMemberEmail("");
      setNewMemberRole("member");
      setIsAddMemberModalOpen(false);
    } catch (err: any) {
      console.error("Failed to add member:", err);
      setAddMemberError(err.response?.data?.detail || "Failed to add member");
    } finally {
      setAddMemberLoading(false);
    }
  };

  const handleCopyInvitationUrl = () => {
    if (invitationUrl) {
      navigator.clipboard.writeText(invitationUrl);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    }
  };

  if (loading) return <OrgSettingsSkeleton />;

  if (error && !organization) {
    return (
      <div className="bg-white min-h-full">
        <div className="w-full mx-auto py-8 px-4 sm:py-12 sm:px-6 lg:px-10">
          <div className="flex items-center gap-2 px-3 py-2.5 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg">
            {error}
          </div>
        </div>
      </div>
    );
  }

  if (!organization) return null;

  return (
    <div className="bg-white h-full flex flex-col overflow-hidden">
      <div className="w-full mx-auto pt-8 sm:pt-12 px-4 sm:px-6 lg:px-10 flex-shrink-0">
        <div className="mb-8">
          <button
            onClick={() => navigate("/home/admin/organizations")}
            className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 transition-colors mb-4"
          >
            <ArrowLeft size={16} />
            Back to organisations
          </button>
          <h3 className="text-xl font-semibold tracking-tight text-gray-900">
            Organisation settings
          </h3>
          <p className="text-base text-gray-500 mt-0.5">{organization.name}</p>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-4 sm:px-6 lg:px-10 pt-4 sm:pt-6 pb-8 sm:pb-12">
        {error && (
          <div className="flex items-center gap-2 px-3 py-2.5 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg mb-6">
            {error}
          </div>
        )}

        <p className="text-xs font-medium uppercase tracking-widest text-gray-400 mb-3">
          Data visibility
        </p>
        <p className="text-sm text-gray-400 mb-3">
          Lock specific bounding-box layers for this organisation. Members and
          organisation admins cannot override these settings.
        </p>
        <div className="border border-gray-200 rounded-lg divide-y divide-gray-100">
          {TOGGLES.map((toggle) => {
            const value = organization[toggle.key];
            return (
              <div
                key={toggle.key}
                className="flex items-start justify-between px-5 py-4 gap-4 max-[600px]:flex-col max-[600px]:gap-3"
              >
                <div>
                  <span className="text-sm font-medium text-gray-900 flex items-center gap-1">
                    {toggle.label}
                    <span className="relative group">
                      <Info className="h-4 w-4 text-gray-400 cursor-help flex-shrink-0" />
                      <span className="absolute bottom-full left-0 sm:left-1/2 sm:-translate-x-1/2 mb-1 hidden group-hover:block w-56 text-xs text-white bg-gray-800 rounded px-2 py-1 sm:text-center z-10">
                        Super-admin only. Affects all members and admins of
                        this organisation.
                      </span>
                    </span>
                  </span>
                  <p className="text-sm text-gray-400 mt-0.5">
                    {toggle.description}
                  </p>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={value}
                  onClick={() => handleToggle(toggle.key)}
                  disabled={saving}
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#1d365d] ${
                    value ? "bg-[#1d365d]" : "bg-gray-200"
                  } ${saving ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
                >
                  <span
                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                      value ? "translate-x-5" : "translate-x-0"
                    }`}
                  />
                </button>
              </div>
            );
          })}
        </div>

        {/* Members */}
        <div className="flex items-center justify-between gap-3 mt-10 mb-3">
          <div>
            <p className="text-xs font-medium uppercase tracking-widest text-gray-400">
              Members
            </p>
            <p className="text-sm text-gray-400 mt-1">
              Add members to this organisation on behalf of its admins.
            </p>
          </div>
          <button
            onClick={() => {
              setAddMemberError(null);
              setIsAddMemberModalOpen(true);
            }}
            className="flex items-center gap-2 px-4 py-2 bg-[#1d365d] text-white rounded-lg hover:bg-[#16294a] transition-colors text-sm font-medium whitespace-nowrap"
          >
            <UserPlus className="w-4 h-4" />
            Add member
          </button>
        </div>

        <div className="border border-gray-200 rounded-lg overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50/50">
                <th className="px-3 sm:px-5 py-2.5 text-left text-xs font-medium uppercase tracking-wide text-gray-400">
                  Name
                </th>
                <th className="px-3 sm:px-5 py-2.5 text-left text-xs font-medium uppercase tracking-wide text-gray-400 hidden sm:table-cell">
                  Email
                </th>
                <th className="px-3 sm:px-5 py-2.5 text-left text-xs font-medium uppercase tracking-wide text-gray-400">
                  Role
                </th>
                <th className="px-3 sm:px-5 py-2.5 text-left text-xs font-medium uppercase tracking-wide text-gray-400 hidden md:table-cell">
                  Joined
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {members.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-5 py-16 text-center">
                    <div className="text-gray-400 text-sm">
                      <p className="font-medium text-gray-600 mb-1">
                        No members yet
                      </p>
                      <p>Add members to start collaborating</p>
                    </div>
                  </td>
                </tr>
              ) : (
                members.map((member) => (
                  <tr
                    key={member.id}
                    className="transition-colors duration-100 hover:bg-gray-50/50"
                  >
                    <td className="px-3 sm:px-5 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-8 w-8 bg-gray-100 rounded-full flex items-center justify-center">
                          <span className="text-sm font-medium text-gray-600">
                            {!member.db_user_id
                              ? "?"
                              : (member.name || member.email || "?")
                                  .charAt(0)
                                  .toUpperCase()}
                          </span>
                        </div>
                        <div className="ml-3">
                          <div className="text-sm font-medium text-gray-900">
                            {!member.db_user_id
                              ? "Pending member"
                              : member.name || "—"}
                          </div>
                          <div className="text-xs text-gray-500 sm:hidden truncate max-w-[120px]">
                            {member.email || "Hidden"}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 sm:px-5 py-4 whitespace-nowrap text-sm text-gray-600 hidden sm:table-cell">
                      {member.email || (
                        <span className="text-gray-400">Hidden</span>
                      )}
                    </td>
                    <td className="px-3 sm:px-5 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-1 sm:gap-2 flex-wrap">
                        <span
                          className={`inline-block px-2 py-1 rounded text-xs font-medium ${getRoleBadgeColor(
                            member.role,
                          )}`}
                        >
                          {member.role}
                        </span>
                        {member.status === "invited" && (
                          <span className="inline-block px-2 py-1 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                            Pending
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-3 sm:px-5 py-4 whitespace-nowrap text-sm text-gray-600 hidden md:table-cell">
                      {formatDate(member.created_at)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          {members.length > 0 && (
            <div className="px-5 py-3 border-t border-gray-100">
              <p className="text-sm text-gray-400">
                {members.length} member{members.length === 1 ? "" : "s"}
              </p>
            </div>
          )}
        </div>
      </div>

      <AddMemberModal
        isOpen={isAddMemberModalOpen}
        setIsOpen={setIsAddMemberModalOpen}
        newMemberEmail={newMemberEmail}
        setNewMemberEmail={setNewMemberEmail}
        newMemberRole={newMemberRole}
        setNewMemberRole={setNewMemberRole}
        addMemberError={addMemberError}
        addMemberLoading={addMemberLoading}
        onAddMember={handleAddMember}
      />

      <InvitationUrlModal
        isOpen={isInvitationModalOpen}
        setIsOpen={setIsInvitationModalOpen}
        invitationUrl={invitationUrl}
        isCopied={isCopied}
        onCopyInvitationUrl={handleCopyInvitationUrl}
      />
    </div>
  );
};

export default AdminOrganizationSettings;
